import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, UserPlus, IdCard, Award, Upload, ShieldCheck, BadgeCheck, Tent, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: UserPlus, title: "Buat Akun", desc: "Klik \"Daftar\" di halaman depan. Pilih \"Pramuka (Anggota)\" untuk peserta didik, atau \"Pembina / Pelatih\" untuk anggota dewasa. Isi nama, email, NTA, dan password (min. 6 karakter). Anda juga dapat masuk dengan akun Google." },
  { icon: IdCard, title: "Lengkapi Profil", desc: "Setelah masuk, buka menu Profil. Isi golongan (Siaga/Penggalang/Penegak/Pandega), Kwarran (kecamatan), Gugus Depan, tanggal dilantik, Satuan Karya (jika ada), dan centang \"Saya Pramuka Garuda\" bila sudah dilantik sebagai Pramuka Garuda." },
  { icon: Award, title: "Tambah Prestasi", desc: "Buka menu Prestasi Saya → \"Tambah Prestasi\". Tulis judul (mis. Juara 1 LT III, TKK Berkemah), tingkat (Ranting s.d. Internasional), kategori, tahun, dan penyelenggara." },
  { icon: Upload, title: "Unggah Sertifikat", desc: "Lampirkan bukti berupa foto/scan sertifikat atau piagam (JPG/PNG/PDF). Pastikan tulisan terbaca jelas agar mudah diverifikasi." },
  { icon: ShieldCheck, title: "Validasi Pembina", desc: "Pembina Gudep Anda akan memeriksa data dan sertifikat. Status berubah dari \"Menunggu Pembina\" menjadi \"Menunggu Kwarcab\" jika valid, atau \"Ditolak\" dengan catatan perbaikan." },
  { icon: BadgeCheck, title: "Verifikasi Kwarcab", desc: "Admin Kwarcab Kota Depok melakukan verifikasi akhir. Prestasi yang disetujui berstatus \"Terverifikasi\" dan tercatat resmi dalam Pusat Data Pramuka Kota Depok." },
];

const TIPS = [
  "Gunakan NTA (Nomor Tanda Anggota) yang tercetak di KTA Anda. NTA bersifat unik dan dapat dipakai untuk login.",
  "Isi Kwarran sesuai kecamatan tempat gugus depan Anda berpangkalan, bukan alamat rumah.",
  "Satu prestasi = satu entri. Jangan menggabungkan beberapa prestasi dalam satu judul.",
  "Jika prestasi ditolak, baca catatan dari Pembina/Kwarcab, hapus entri lama, lalu ajukan kembali dengan data yang benar.",
  "Data dapat diperbarui kapan saja melalui menu Profil.",
];

export default function GuidePage() {
  return (
    <div className="min-h-screen">
      <header className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="guide-back-home">
          <div className="h-9 w-9 rounded-xl bg-[#4A2E2B] flex items-center justify-center"><Tent className="h-5 w-5 text-[#E5A93C]" /></div>
          <div className="font-display font-extrabold text-[#1F1917] text-sm">SIPRAM DEPOK</div>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A2E2B] hover:underline"><ArrowLeft className="h-4 w-4" /> Kembali</Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24" data-testid="guide-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-xs uppercase tracking-widest font-bold text-[#B85C38] mb-2">Panduan</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#1F1917] leading-tight">Petunjuk Pengisian Data</h1>
          <p className="mt-4 text-[#4A423F] max-w-2xl leading-relaxed">
            Ikuti enam langkah berikut agar data diri dan prestasi Anda tercatat resmi di Pusat Data Pramuka Kota Depok.
          </p>
        </motion.div>

        <ol className="mt-10 space-y-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.li key={s.title} data-testid={`guide-step-${i + 1}`}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-[#E5DFD7] shadow-sm p-5 sm:p-6 flex gap-5">
                <div className="relative shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-[#4A2E2B] flex items-center justify-center"><Icon className="h-5 w-5 text-[#E5A93C]" /></div>
                  <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-[#E5A93C] text-[#2A1F1B] text-xs font-extrabold flex items-center justify-center">{i + 1}</div>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#1F1917]">{s.title}</h3>
                  <p className="text-sm text-[#4A423F] leading-relaxed mt-1">{s.desc}</p>
                </div>
              </motion.li>
            );
          })}
        </ol>

        <div className="mt-10 bg-[#2A1F1B] text-white rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-lg font-semibold text-[#E5A93C] mb-3">Tips Penting</h2>
          <ul className="space-y-2 text-sm text-white/85 list-disc pl-5">
            {TIPS.map((t) => <li key={t}>{t}</li>)}
          </ul>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/auth" data-testid="guide-cta-daftar" className="inline-flex items-center gap-2 bg-[#E5A93C] text-[#2A1F1B] px-6 py-3.5 rounded-full font-bold hover:bg-[#d69a2b] transition-colors">
            Mulai Isi Data <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}
